import { useState } from "react";
import PropDealsForm from "./ProptDealsForm";

const LandingPage = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Real Estate Investment Hub</h1>
        <p style={styles.subtitle}>
          Unlock Off-Market Deals, Creative Financing, and Smart Investment
          Tools
        </p>
      </header>

      <main style={styles.main}>
        {/* Placeholder for Existing React Form */}
        <section style={styles.formSection}>
          <h2 style={styles.sectionTitle}>Get Your Free Property Quote</h2>
          <div id="react-form-placeholder" style={styles.formPlaceholder}>
            <PropDealsForm />
          </div>
        </section>

        {/* Benefits of Off-Market Properties */}
        <section style={styles.section}>
          <button
            style={styles.expandButton}
            onClick={() => toggleSection("offMarketBenefits")}
            aria-expanded={expandedSections["offMarketBenefits"]}
            aria-controls="offMarketBenefits"
          >
            {expandedSections["offMarketBenefits"] ? "▼" : "▶"} Benefits of
            Off-Market Properties
          </button>
          {expandedSections["offMarketBenefits"] && (
            <div id="offMarketBenefits" style={styles.expandedContent}>
              <h3>Sellers</h3>
              <ul style={styles.list}>
                <li>
                  Privacy: Avoid public exposure, ideal for discreet sales
                  (e.g., divorce, relocation).
                </li>
                <li>
                  Speed: Close in weeks without open houses or extensive
                  marketing.
                </li>
                <li>
                  Low Costs: Skip staging, repairs, or photography, reducing
                  expenses.
                </li>
                <li>
                  Quality Buyers: Access pre-qualified investors for reliable
                  offers.
                </li>
                <li>
                  Stability: Fewer parties lower the risk of deal fallout.
                </li>
              </ul>
              <h3>Buyers/Investors</h3>
              <ul style={styles.list}>
                <li>
                  Less Competition: Exclusive deals enable better price
                  negotiations.
                </li>
                <li>
                  Evaluation Time: More room for inspections without bidding
                  wars.
                </li>
                <li>
                  Cost Savings: Potential for below-market prices on distressed
                  properties.
                </li>
                <li>
                  Unique Assets: Access to commercial or rare properties not
                  publicly listed.
                </li>
              </ul>
              <p style={styles.note}>
                Note: Off-market sales prioritize speed but may yield lower
                prices due to limited buyer pools.
              </p>
            </div>
          )}
        </section>

        {/* Investment Strategies */}
        <section style={styles.section}>
          <button
            style={styles.expandButton}
            onClick={() => toggleSection("investmentStrategies")}
            aria-expanded={expandedSections["investmentStrategies"]}
            aria-controls="investmentStrategies"
          >
            {expandedSections["investmentStrategies"] ? "▼" : "▶"} Quick Cash
            Buyer Strategies
          </button>
          {expandedSections["investmentStrategies"] && (
            <div id="investmentStrategies" style={styles.expandedContent}>
              <p>
                "We Buy Houses" programs (e.g., Opendoor, Offerpad) target
                motivated sellers with fast, cash offers for "as-is" properties,
                closing in 7–30 days. Investors acquire at 55–85% of ARV for
                profit via resale or rentals.
              </p>
              <h3>Business Models</h3>
              <ul style={styles.list}>
                <li>
                  Direct Cash Investors: Buy fixer-uppers outright (e.g., We Buy
                  Ugly Houses).
                </li>
                <li>
                  iBuyers: Algorithm-driven instant offers (e.g., Opendoor).
                </li>
                <li>
                  Wholesalers: Assign contracts for fees without ownership.
                </li>
                <li>
                  Bridge Loan Hybrids: Offer equity advances with backup
                  purchases (e.g., Knock).
                </li>
                <li>
                  Auction Platforms: Competitive investor bids (e.g., Sundae).
                </li>
              </ul>
              <h3>Strategies</h3>
              <ol style={styles.list}>
                <li>
                  Fix-and-Flip: Buy at 65–75% ARV, renovate, sell for 10–20%
                  margins.
                </li>
                <li>Wholesaling: Assign contracts for $5K–$20K fees.</li>
                <li>
                  Buy-and-Hold: Rent at 1% of purchase price for cash flow.
                </li>
                <li>
                  iBuyer Offers: Quick buys at 90–95% value, profit via fees.
                </li>
                <li>Seller Financing: Installment sales for flexible entry.</li>
                <li>Bridge Financing: Short-term loans with 2–2.5% fees.</li>
              </ol>
              <p style={styles.note}>
                Profits come from discounts and volume; risks are hedged with
                cash deals. Sellers trade 10–20% value for speed.
              </p>
            </div>
          )}
        </section>

        {/* Investor Keywords */}
        <section style={styles.section}>
          <button
            style={styles.expandButton}
            onClick={() => toggleSection("keywords")}
            aria-expanded={expandedSections["keywords"]}
            aria-controls="keywords"
          >
            {expandedSections["keywords"] ? "▼" : "▶"} Investor & Seller
            Keywords
          </button>
          {expandedSections["keywords"] && (
            <div id="keywords" style={styles.expandedContent}>
              <h3>Investor Buyers</h3>
              <ul style={styles.list}>
                <li>Off-market properties</li>
                <li>Distressed properties</li>
                <li>Motivated seller</li>
                <li>Fixer-upper</li>
                <li>Foreclosure</li>
                <li>Wholesale deals</li>
                <li>Houses for sale [location]</li>
                <li>Tax auction properties</li>
                <li>We buy houses [location]</li>
                <li>Quick cash offers</li>
              </ul>
              <h3>Sellers</h3>
              <ul style={styles.list}>
                <li>Sell house fast</li>
                <li>Cash for houses</li>
                <li>Sell my property fast</li>
                <li>We buy ugly houses</li>
                <li>Quick sale no repairs</li>
                <li>Home for sale as-is</li>
                <li>Motivated seller discount</li>
                <li>Cash buyer needed</li>
                <li>Avoid foreclosure sale</li>
                <li>Fast closing cash deal</li>
              </ul>
              <p style={styles.note}>
                These terms connect sellers and investors on platforms like
                Craigslist or investor sites.
              </p>
            </div>
          )}
        </section>

        {/* Creative Financing Keywords */}
        <section style={styles.section}>
          <button
            style={styles.expandButton}
            onClick={() => toggleSection("creativeFinancingKeywords")}
            aria-expanded={expandedSections["creativeFinancingKeywords"]}
            aria-controls="creativeFinancingKeywords"
          >
            {expandedSections["creativeFinancingKeywords"] ? "▼" : "▶"} Creative
            Financing Keywords
          </button>
          {expandedSections["creativeFinancingKeywords"] && (
            <div id="creativeFinancingKeywords" style={styles.expandedContent}>
              <ul style={styles.list}>
                <li>
                  <strong>Seller Financing</strong>: Seller carryback, vendor
                  take-back mortgage, installment sale
                </li>
                <li>
                  <strong>Lease Options</strong>: Lease-to-own, rent-to-own,
                  option to purchase
                </li>
                <li>
                  <strong>Hard Money</strong>: Hard money lender, bridge loan
                  for flip, asset-based loan
                </li>
                <li>
                  <strong>HELOC</strong>: Home equity line of credit, cash-out
                  equity
                </li>
                <li>
                  <strong>Private Money</strong>: Private lender investor, angel
                  investor financing
                </li>
                <li>
                  <strong>Crowdfunding</strong>: Real estate crowdfunding,
                  Fundrise deals
                </li>
                <li>
                  <strong>Joint Ventures</strong>: JV partnership real estate,
                  equity share deal
                </li>
                <li>
                  <strong>Subject-To</strong>: Sub2 financing, take over
                  payments
                </li>
                <li>
                  <strong>Mezzanine Debt</strong>: Preferred equity investment,
                  hybrid debt equity
                </li>
                <li>
                  <strong>Government Programs</strong>: FHA 203k loan, USDA
                  rural financing
                </li>
              </ul>
              <p style={styles.note}>
                Use these in searches or ads to explore financing options in
                high-rate markets.
              </p>
            </div>
          )}
        </section>

        {/* Creative Financing Techniques */}
        <section style={styles.section}>
          <button
            style={styles.expandButton}
            onClick={() => toggleSection("creativeFinancing")}
            aria-expanded={expandedSections["creativeFinancing"]}
            aria-controls="creativeFinancing"
          >
            {expandedSections["creativeFinancing"] ? "▼" : "▶"} Creative
            Financing Techniques
          </button>
          {expandedSections["creativeFinancing"] && (
            <div id="creativeFinancing" style={styles.expandedContent}>
              <p>
                Creative financing helps investors close deals with minimal
                capital in 2025's high-rate environment.
              </p>
              <h3>Joint Venture (JV) Partnerships</h3>
              <ul style={styles.list}>
                <li>Pool resources for large deals (e.g., multi-family).</li>
                <li>2025 Trend: Combines with bridge loans for 15–25% IRR.</li>
                <li>Pros: Diversifies risk; Cons: Potential disputes.</li>
              </ul>
              <h3>Vendor Takeback Mortgages</h3>
              <ul style={styles.list}>
                <li>Seller finances 20–50% of price at 8–12% interest.</li>
                <li>
                  2025 Trend: Popular for off-market and commercial deals.
                </li>
                <li>Pros: Low closing costs; Cons: Higher rates than banks.</li>
              </ul>
              <h3>MLI Select Program (Canada)</h3>
              <ul style={styles.list}>
                <li>5% down for multi-unit properties via CMHC insurance.</li>
                <li>
                  2025 Trend: Fuels value-add multi-family with green
                  incentives.
                </li>
                <li>Pros: Low entry; Cons: Canada-only, strict criteria.</li>
              </ul>
              <h3>Other Modes</h3>
              <ul style={styles.list}>
                <li>Bridge Loans: 10–15% rates for quick flips.</li>
                <li>Mezzanine Debt: 12–18% returns for commercial gaps.</li>
                <li>Crowdfunding: $500 minimums for 8–12% yields.</li>
                <li>Assumable Mortgages: Take over low-rate loans (3–4%).</li>
              </ul>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th>Technique</th>
                    <th>Down Payment</th>
                    <th>Ideal For</th>
                    <th>Cost/Rate (2025)</th>
                    <th>Risk</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>JV Partnerships</td>
                    <td>Varies</td>
                    <td>Multi-family</td>
                    <td>Profit split</td>
                    <td>Medium</td>
                  </tr>
                  <tr>
                    <td>Vendor Takeback</td>
                    <td>10–20%</td>
                    <td>Off-market</td>
                    <td>8–12%</td>
                    <td>Low-Medium</td>
                  </tr>
                  <tr>
                    <td>MLI Select</td>
                    <td>5%</td>
                    <td>Canadian multi-unit</td>
                    <td>Lender-dependent</td>
                    <td>Low</td>
                  </tr>
                  <tr>
                    <td>Bridge Loans</td>
                    <td>20–30%</td>
                    <td>Flips</td>
                    <td>10–15%</td>
                    <td>High</td>
                  </tr>
                  <tr>
                    <td>Mezzanine Debt</td>
                    <td>N/A</td>
                    <td>Commercial</td>
                    <td>12–18%</td>
                    <td>High</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Commercial and Multi-Family Investments */}
        <section style={styles.section}>
          <button
            style={styles.expandButton}
            onClick={() => toggleSection("commercialMultiFamily")}
            aria-expanded={expandedSections["commercialMultiFamily"]}
            aria-controls="commercialMultiFamily"
          >
            {expandedSections["commercialMultiFamily"] ? "▼" : "▶"} Commercial &
            Multi-Family Investments
          </button>
          {expandedSections["commercialMultiFamily"] && (
            <div id="commercialMultiFamily" style={styles.expandedContent}>
              <p>
                Commercial real estate (CRE) and multi-family (5+ units) thrive
                in 2025 with strong demand (3–5% rent growth).
              </p>
              <h3>Strategies</h3>
              <ol style={styles.list}>
                <li>
                  Value-Add: Buy at 80–90% value, renovate for 20–30% uplift.
                </li>
                <li>Core-Plus: Stable assets with 6–9% yields.</li>
                <li>Development: High-risk builds with JV financing.</li>
                <li>Opportunistic: Distressed CRE via auctions.</li>
                <li>
                  Syndications: Passive investments via CrowdStreet (8–12%
                  returns).
                </li>
              </ol>
              <h3>2025 Trends</h3>
              <ul style={styles.list}>
                <li>
                  Multi-Family: 9–12% returns in Sun Belt; risks from insurance
                  costs.
                </li>
                <li>
                  Commercial: Industrial booms, offices lag (20%+ vacancy).
                </li>
                <li>
                  Financing: Seller financing at 70% LTV; 5–6% rates for
                  multi-family.
                </li>
              </ul>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th>Sector</th>
                    <th>Cap Rate</th>
                    <th>Growth Driver</th>
                    <th>Risk</th>
                    <th>Strategy</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Multi-Family</td>
                    <td>5–6.5%</td>
                    <td>Housing shortages</td>
                    <td>Oversupply</td>
                    <td>Value-Add</td>
                  </tr>
                  <tr>
                    <td>Office</td>
                    <td>7–9%</td>
                    <td>Hybrid work</td>
                    <td>High vacancy</td>
                    <td>Opportunistic</td>
                  </tr>
                  <tr>
                    <td>Retail</td>
                    <td>6–7.5%</td>
                    <td>E-commerce</td>
                    <td>Consumer shifts</td>
                    <td>Core-Plus</td>
                  </tr>
                  <tr>
                    <td>Industrial</td>
                    <td>4.5–6%</td>
                    <td>Logistics</td>
                    <td>Rate sensitivity</td>
                    <td>Development</td>
                  </tr>
                </tbody>
              </table>
              <p style={styles.note}>
                Start with syndications or Class B multi-family for 15–20%
                equity growth.
              </p>
            </div>
          )}
        </section>

        {/* Calculators Section */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Investment Calculators</h2>
          <div style={styles.calculatorGrid}>
            {/* Fix-and-Flip Calculator */}
            <div style={styles.calculatorContainer}>
              <h3 style={styles.calculatorTitle}>Fix-and-Flip Profit</h3>
              <form style={styles.form}>
                <label style={styles.label}>
                  Purchase Price ($):{" "}
                  <input
                    type="number"
                    id="purchase"
                    required
                    min="0"
                    step="0.01"
                    style={styles.input}
                  />
                </label>
                <label style={styles.label}>
                  Repair Costs ($):{" "}
                  <input
                    type="number"
                    id="repairs"
                    required
                    min="0"
                    step="0.01"
                    style={styles.input}
                  />
                </label>
                <label style={styles.label}>
                  ARV ($):{" "}
                  <input
                    type="number"
                    id="arv"
                    required
                    min="0"
                    step="0.01"
                    style={styles.input}
                  />
                </label>
                <label style={styles.label}>
                  Holding Costs ($):{" "}
                  <input
                    type="number"
                    id="holding"
                    min="0"
                    step="0.01"
                    value="0"
                    style={styles.input}
                  />
                </label>
                <label style={styles.label}>
                  Selling Costs ($):{" "}
                  <input
                    type="number"
                    id="selling"
                    min="0"
                    step="0.01"
                    value="0"
                    style={styles.input}
                  />
                </label>
                <button
                  type="button"
                  onClick={calculateFlip}
                  style={styles.button}
                >
                  Calculate
                </button>
              </form>
              <div id="flipResult" style={styles.result}></div>
            </div>

            {/* Vendor Takeback Calculator */}
            <div style={styles.calculatorContainer}>
              <h3 style={styles.calculatorTitle}>Vendor Takeback Payment</h3>
              <form style={styles.form}>
                <label style={styles.label}>
                  Principal ($):{" "}
                  <input
                    type="number"
                    id="principal"
                    required
                    min="0"
                    step="0.01"
                    style={styles.input}
                  />
                </label>
                <label style={styles.label}>
                  Interest Rate (%):{" "}
                  <input
                    type="number"
                    id="rate"
                    required
                    min="0"
                    step="0.1"
                    style={styles.input}
                  />
                </label>
                <label style={styles.label}>
                  Term (Years):{" "}
                  <input
                    type="number"
                    id="years"
                    required
                    min="1"
                    step="1"
                    style={styles.input}
                  />
                </label>
                <button
                  type="button"
                  onClick={calculatePayment}
                  style={styles.button}
                >
                  Calculate
                </button>
              </form>
              <div id="paymentResult" style={styles.result}></div>
            </div>

            {/* Cap Rate Calculator */}
            <div style={styles.calculatorContainer}>
              <h3 style={styles.calculatorTitle}>Cap Rate</h3>
              <form style={styles.form}>
                <label style={styles.label}>
                  NOI ($):{" "}
                  <input
                    type="number"
                    id="noi"
                    required
                    min="0"
                    step="0.01"
                    style={styles.input}
                  />
                </label>
                <label style={styles.label}>
                  Property Value ($):{" "}
                  <input
                    type="number"
                    id="value"
                    required
                    min="0"
                    step="0.01"
                    style={styles.input}
                  />
                </label>
                <button
                  type="button"
                  onClick={calculateCap}
                  style={styles.button}
                >
                  Calculate
                </button>
              </form>
              <div id="capResult" style={styles.result}></div>
            </div>

            {/* Rental Cash Flow Calculator */}
            <div style={styles.calculatorContainer}>
              <h3 style={styles.calculatorTitle}>Rental Cash Flow</h3>
              <form style={styles.form}>
                <label style={styles.label}>
                  Rental Income ($):{" "}
                  <input
                    type="number"
                    id="income"
                    required
                    min="0"
                    step="0.01"
                    style={styles.input}
                  />
                </label>
                <label style={styles.label}>
                  Expenses ($):{" "}
                  <input
                    type="number"
                    id="expenses"
                    required
                    min="0"
                    step="0.01"
                    style={styles.input}
                  />
                </label>
                <label style={styles.label}>
                  Mortgage ($):{" "}
                  <input
                    type="number"
                    id="mortgage"
                    min="0"
                    step="0.01"
                    value="0"
                    style={styles.input}
                  />
                </label>
                <button
                  type="button"
                  onClick={calculateCashFlow}
                  style={styles.button}
                >
                  Calculate
                </button>
              </form>
              <div id="cashFlowResult" style={styles.result}></div>
            </div>
          </div>
        </section>
      </main>

      <footer style={styles.footer}>
        <p>&copy; 2025 Real Estate Investment Hub. All rights reserved.</p>
      </footer>
    </div>
  );
};

// Calculator Functions
const calculateFlip = () => {
  const purchase = parseFloat(document.getElementById("purchase").value) || 0;
  const repairs = parseFloat(document.getElementById("repairs").value) || 0;
  const arv = parseFloat(document.getElementById("arv").value) || 0;
  const holding = parseFloat(document.getElementById("holding").value) || 0;
  const selling = parseFloat(document.getElementById("selling").value) || 0;

  if (purchase <= 0 || repairs < 0 || arv <= 0) {
    document.getElementById("flipResult").innerHTML =
      '<span style="color: #d32f2f;">Please enter valid positive values for Purchase Price and ARV.</span>';
    return;
  }

  const totalCost = purchase + repairs + holding + selling;
  const profit = arv - totalCost;
  const roi = totalCost > 0 ? (profit / totalCost) * 100 : 0;

  document.getElementById("flipResult").innerHTML = `
    Estimated Profit: $${profit.toFixed(2)}<br>
    ROI: ${roi.toFixed(2)}%
  `;
};

const calculatePayment = () => {
  const principal = parseFloat(document.getElementById("principal").value) || 0;
  const rate = parseFloat(document.getElementById("rate").value) || 0;
  const years = parseInt(document.getElementById("years").value) || 0;

  if (principal <= 0 || rate < 0 || years <= 0) {
    document.getElementById("paymentResult").innerHTML =
      '<span style="color: #d32f2f;">Please enter valid positive values for all fields.</span>';
    return;
  }

  const monthlyRate = rate / 12 / 100;
  const months = years * 12;
  let payment;
  if (monthlyRate === 0) {
    payment = principal / months;
  } else {
    payment =
      (principal * (monthlyRate * Math.pow(1 + monthlyRate, months))) /
      (Math.pow(1 + monthlyRate, months) - 1);
  }

  document.getElementById(
    "paymentResult"
  ).innerHTML = `Monthly Payment: $${payment.toFixed(2)}`;
};

const calculateCap = () => {
  const noi = parseFloat(document.getElementById("noi").value) || 0;
  const value = parseFloat(document.getElementById("value").value) || 0;

  if (noi < 0 || value <= 0) {
    document.getElementById("capResult").innerHTML =
      '<span style="color: #d32f2f;">Please enter a valid positive value for Property Value.</span>';
    return;
  }

  const capRate = value > 0 ? (noi / value) * 100 : 0;

  document.getElementById("capResult").innerHTML = `Cap Rate: ${capRate.toFixed(
    2
  )}%`;
};

const calculateCashFlow = () => {
  const income = parseFloat(document.getElementById("income").value) || 0;
  const expenses = parseFloat(document.getElementById("expenses").value) || 0;
  const mortgage = parseFloat(document.getElementById("mortgage").value) || 0;

  if (income <= 0 || expenses < 0) {
    document.getElementById("cashFlowResult").innerHTML =
      '<span style="color: #d32f2f;">Please enter valid positive values for Income.</span>';
    return;
  }

  const cashFlow = income - expenses - mortgage;

  document.getElementById(
    "cashFlowResult"
  ).innerHTML = `Monthly Cash Flow: $${cashFlow.toFixed(2)}`;
};

// Responsive Styles
const styles = {
  container: {
    fontFamily: "'Arial', sans-serif",
    lineHeight: 1.6,
    margin: 0,
    padding: 0,
    backgroundColor: "#f4f4f4",
    color: "#333",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    backgroundColor: "#005f73",
    color: "#fff",
    padding: "clamp(20px, 5vw, 40px) clamp(10px, 3vw, 20px)",
    textAlign: "center",
  },
  title: {
    margin: 0,
    fontSize: "clamp(1.8rem, 5vw, 2.5rem)",
  },
  subtitle: {
    margin: "10px 0 0",
    fontSize: "clamp(0.9rem, 3vw, 1.2rem)",
  },
  main: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "clamp(10px, 3vw, 20px)",
    flex: 1,
  },
  formSection: {
    backgroundColor: "#fff",
    padding: "clamp(15px, 4vw, 20px)",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    marginBottom: "clamp(15px, 4vw, 30px)",
  },
  sectionTitle: {
    fontSize: "clamp(1.3rem, 4vw, 1.8rem)",
    marginBottom: "clamp(10px, 2vw, 15px)",
    color: "#005f73",
  },
  formPlaceholder: {
    textAlign: "center",
  },
  placeholderText: {
    color: "#888",
    fontSize: "clamp(0.8rem, 2.5vw, 1rem)",
  },
  section: {
    marginBottom: "clamp(10px, 3vw, 20px)",
  },
  expandButton: {
    width: "100%",
    padding: "clamp(10px, 2.5vw, 15px)",
    backgroundColor: "#0a9396",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "clamp(0.9rem, 2.5vw, 1.1rem)",
    textAlign: "left",
    transition: "background 0.3s, transform 0.2s",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  expandedContent: {
    backgroundColor: "#fff",
    padding: "clamp(10px, 3vw, 20px)",
    borderRadius: "0 0 8px 8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    fontSize: "clamp(0.85rem, 2.5vw, 1rem)",
  },
  list: {
    paddingLeft: "clamp(15px, 4vw, 20px)",
    margin: "10px 0",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    margin: "clamp(5px, 2vw, 10px) 0",
    fontSize: "clamp(0.8rem, 2.5vw, 0.9rem)",
  },
  note: {
    fontStyle: "italic",
    color: "#555",
    fontSize: "clamp(0.8rem, 2.5vw, 0.95rem)",
  },
  calculatorGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(clamp(250px, 45vw, 300px), 1fr))",
    gap: "clamp(10px, 3vw, 20px)",
  },
  calculatorContainer: {
    backgroundColor: "#fff",
    padding: "clamp(10px, 3vw, 20px)",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  calculatorTitle: {
    margin: "0 0 clamp(8px, 2vw, 15px)",
    textAlign: "center",
    color: "#333",
    fontSize: "clamp(1rem, 3vw, 1.3rem)",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "clamp(8px, 2vw, 12px)",
  },
  label: {
    display: "flex",
    flexDirection: "column",
    fontSize: "clamp(0.8rem, 2.5vw, 0.9rem)",
    color: "#555",
  },
  input: {
    padding: "clamp(6px, 2vw, 8px)",
    border: "1px solid #ccc",
    borderRadius: "4px",
    fontSize: "clamp(0.8rem, 2.5vw, 1rem)",
    boxSizing: "border-box",
    width: "100%",
  },
  button: {
    padding: "clamp(8px, 2vw, 10px)",
    background: "#0a9396",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "clamp(0.8rem, 2.5vw, 1rem)",
    transition: "background 0.3s",
  },
  result: {
    marginTop: "clamp(8px, 2vw, 15px)",
    fontWeight: "bold",
    color: "#333",
    textAlign: "center",
    fontSize: "clamp(0.8rem, 2.5vw, 1rem)",
  },
  footer: {
    backgroundColor: "#005f73",
    color: "#fff",
    textAlign: "center",
    padding: "clamp(10px, 3vw, 20px)",
    marginTop: "clamp(20px, 5vw, 40px)",
    fontSize: "clamp(0.8rem, 2.5vw, 0.9rem)",
  },
  // Media Queries (inline equivalent)
  "@media (max-width: 768px)": {
    title: { fontSize: "clamp(1.5rem, 4vw, 2rem)" },
    subtitle: { fontSize: "clamp(0.8rem, 2.5vw, 1rem)" },
    sectionTitle: { fontSize: "clamp(1.2rem, 3.5vw, 1.5rem)" },
    expandButton: { fontSize: "clamp(0.85rem, 2.5vw, 1rem)" },
    calculatorGrid: { gridTemplateColumns: "1fr" },
    calculatorContainer: { padding: "clamp(8px, 2.5vw, 15px)" },
  },
  "@media (max-width: 600px)": {
    header: { padding: "clamp(15px, 4vw, 30px) clamp(8px, 2.5vw, 15px)" },
    main: { padding: "clamp(8px, 2.5vw, 15px)" },
    formSection: { padding: "clamp(10px, 2.5vw, 15px)" },
    calculatorTitle: { fontSize: "clamp(0.9rem, 2.5vw, 1.1rem)" },
    input: { fontSize: "clamp(0.75rem, 2.5vw, 0.9rem)" },
    button: { fontSize: "clamp(0.75rem, 2.5vw, 0.9rem)" },
  },
  "@media (max-width: 320px)": {
    title: { fontSize: "clamp(1.2rem, 4vw, 1.5rem)" },
    subtitle: { fontSize: "clamp(0.7rem, 2.5vw, 0.9rem)" },
    sectionTitle: { fontSize: "clamp(1rem, 3vw, 1.2rem)" },
    expandButton: { padding: "clamp(8px, 2.5vw, 10px)" },
    expandedContent: { padding: "clamp(8px, 2.5vw, 15px)" },
  },
};

export default LandingPage;
